import { useRef, type KeyboardEvent } from 'react';
import { MousePointer2, Square, Undo2, Redo2, ZoomIn, ZoomOut } from 'lucide-react';
import type { EditorFace } from './occlusionStudy';

/** More tools arrive one per task (elipse) — see tasks.md §6.1/§7. */
export type OcclusionTool = 'select' | 'rectangle';

/** 0.25 (25%) per click → 7 clean stops between the two bounds: 50/75/100/125/150/175/200. */
export const OCCLUSION_ZOOM_MIN = 0.5;
export const OCCLUSION_ZOOM_MAX = 2;
export const OCCLUSION_ZOOM_STEP = 0.25;

interface ToolOption {
  value: OcclusionTool;
  label: string;
  icon: typeof MousePointer2;
}

const TOOL_OPTIONS: ToolOption[] = [
  { value: 'select', label: 'Selecionar', icon: MousePointer2 },
  { value: 'rectangle', label: 'Retângulo', icon: Square },
];

const FACE_OPTIONS: { label: string; value: EditorFace }[] = [
  { label: 'Editar', value: 'edit' },
  { label: 'Aluno vê frente', value: 'front' },
  { label: 'Aluno vê verso', value: 'back' },
];

export interface OcclusionToolbarProps {
  tool: OcclusionTool;
  onToolChange: (tool: OcclusionTool) => void;
  face: EditorFace;
  onFaceChange: (face: EditorFace) => void;
  /** False ⇒ nothing to undo — the button is present but disabled, never removed from the DOM. */
  podeDesfazer: boolean;
  onDesfazer: () => void;
  /** False ⇒ nothing to redo. */
  podeRefazer: boolean;
  onRefazer: () => void;
  /** Current factor, already clamped to [OCCLUSION_ZOOM_MIN, OCCLUSION_ZOOM_MAX]; 1 = 100%. */
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

/**
 * Tool palette + the Editar/Aluno·frente/Aluno·verso segmented control, as one
 * horizontally-scrollable row so nothing is clipped at 375px (no frame covers
 * the narrow editor — proposal S10). Elipse is not rendered here yet — it
 * lands wired, in its own task (design, "O editor cresce bastante").
 * Desfazer/Refazer and Zoom (Ampliar) sit between Ferramentas and the face
 * control, in the order the spec lists them.
 */
export default function OcclusionToolbar({
  tool,
  onToolChange,
  face,
  onFaceChange,
  podeDesfazer,
  onDesfazer,
  podeRefazer,
  onRefazer,
  zoom,
  onZoomChange,
}: OcclusionToolbarProps) {
  const faceButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleFaceKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = FACE_OPTIONS.findIndex((option) => option.value === face);
    if (currentIndex === -1) return;

    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % FACE_OPTIONS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + FACE_OPTIONS.length) % FACE_OPTIONS.length;
        break;
      default:
        return;
    }

    event.preventDefault();
    onFaceChange(FACE_OPTIONS[nextIndex].value);
    faceButtonRefs.current[nextIndex]?.focus();
  };

  const handleZoomOut = () => {
    const next = Math.round((zoom - OCCLUSION_ZOOM_STEP) * 100) / 100;
    onZoomChange(Math.max(OCCLUSION_ZOOM_MIN, next));
  };

  const handleZoomIn = () => {
    const next = Math.round((zoom + OCCLUSION_ZOOM_STEP) * 100) / 100;
    onZoomChange(Math.min(OCCLUSION_ZOOM_MAX, next));
  };

  return (
    <div
      data-occlusion-toolbar=""
      className="flex w-full min-w-0 items-center gap-3 overflow-x-auto rounded-input border border-edge bg-surface-muted p-1.5"
    >
      <div role="group" aria-label="Ferramentas" className="flex shrink-0 items-center gap-1">
        {TOOL_OPTIONS.map((option) => {
          const Icon = option.icon;
          const active = tool === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              aria-label={option.label}
              onClick={() => onToolChange(option.value)}
              className={
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-input border px-2.5 py-1.5 text-xs font-semibold ' +
                (active
                  ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                  : 'border-transparent text-ink-secondary hover:bg-surface-hover hover:text-ink')
              }
            >
              <Icon size={15} className={active ? 'text-select' : ''} aria-hidden />
              {active && option.label}
            </button>
          );
        })}
      </div>

      <div role="group" aria-label="Desfazer e refazer" className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label="Desfazer"
          disabled={!podeDesfazer}
          onClick={onDesfazer}
          className="flex shrink-0 items-center justify-center rounded-input border border-transparent p-1.5 text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:cursor-not-allowed disabled:text-ink-muted disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-ink-muted"
        >
          <Undo2 size={15} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Refazer"
          disabled={!podeRefazer}
          onClick={onRefazer}
          className="flex shrink-0 items-center justify-center rounded-input border border-transparent p-1.5 text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:cursor-not-allowed disabled:text-ink-muted disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-ink-muted"
        >
          <Redo2 size={15} aria-hidden />
        </button>
      </div>

      <div role="group" aria-label="Zoom" className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label="Reduzir zoom"
          onClick={handleZoomOut}
          disabled={zoom <= OCCLUSION_ZOOM_MIN}
          className="flex shrink-0 items-center rounded-input border border-transparent px-2.5 py-1.5 text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ZoomOut size={15} aria-hidden />
        </button>
        <span
          data-occlusion-zoom={Math.round(zoom * 100)}
          aria-live="polite"
          className="w-10 shrink-0 text-center text-xs font-semibold tabular-nums text-ink-secondary"
        >
          {`${Math.round(zoom * 100)}%`}
        </span>
        <button
          type="button"
          aria-label="Aumentar zoom"
          onClick={handleZoomIn}
          disabled={zoom >= OCCLUSION_ZOOM_MAX}
          className="flex shrink-0 items-center rounded-input border border-transparent px-2.5 py-1.5 text-ink-secondary hover:bg-surface-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ZoomIn size={15} aria-hidden />
        </button>
      </div>

      {/* bg-surface, not bg-surface-muted like CompositorPreview's otherwise-identical
          segmented control: this one nests inside the toolbar's own bg-surface-muted bar,
          and surface-muted-on-surface-muted would blend into it in both themes. */}
      <div
        role="radiogroup"
        aria-label="O que a imagem mostra"
        onKeyDown={handleFaceKeyDown}
        className="flex shrink-0 items-center gap-0.5 rounded-full border border-edge bg-surface p-0.5"
      >
        {FACE_OPTIONS.map((option, index) => {
          const chosen = face === option.value;
          return (
            <button
              key={option.value}
              ref={(element) => {
                faceButtonRefs.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={chosen}
              tabIndex={chosen ? 0 : -1}
              onClick={() => onFaceChange(option.value)}
              className={
                'shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold ' +
                (chosen
                  ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                  : 'border-transparent text-ink-muted hover:text-ink')
              }
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
